import React, { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

import reportWebVitals from './reportWebVitals';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';

// ✅ All image imports at top — satisfies ESLint import/first rule
import herobg from './images/herobg.jpg';
import shield from './images/shield.png';
import shield2x from './images/shield@2x.png';
import permit from './images/permit.png';
import permit2x from './images/permit@2x.png';
import check from './images/check.png';

// Eagerly loaded (above-the-fold / always needed)
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Hero from './components/Hero';
import Loader from "./components/Loader";
import { captureTrackingParams } from './utils/tracking';

captureTrackingParams();

// ✅ Preload Hero images immediately — before any render
const _preloadCache = new Map();
[herobg, shield, shield2x, permit, permit2x, check].forEach((src) => {
  const img = new Image();
  img.src = src;
  _preloadCache.set(src, img); // Hold strong reference — prevents GC drop
});

// ─── Lazy components with named preload() ────────────────────────────────────
// Each factory is stored so we can call it early (on hover/idle) to kick off
// the network request before the user finishes clicking.

const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory; // expose factory so callers can trigger fetch
  return Component;
};

const Trustbar = lazyWithPreload(() => import('./components/Trustbar'));
const Safety = lazyWithPreload(() => import('./components/Safety'));
const HowItWorks = lazyWithPreload(() => import('./components/HowItWorks'));
const DontWait = lazyWithPreload(() => import('./components/DontWait'));
const Testimonials = lazyWithPreload(() => import('./components/Testimonials'));
const FAQSection = lazyWithPreload(() => import('./components/FAQSection'));
const EligibilityCheck = lazyWithPreload(() => import('./components/EligibilityCheck'));
const QualificationFlow = lazyWithPreload(() => import('./components/QualificationFlow'));
const Shipping = lazyWithPreload(() => import('./components/Shipping'));
const Checkout = lazyWithPreload(() => import('./components/Checkout'));
const Confirmation = lazyWithPreload(() => import('./components/Confirmation'));
const QualifyBanner = lazyWithPreload(() => import('./components/QualifyBanner'));
const Important = lazyWithPreload(() => import('./components/Important'));
const CheckoutApplePay = lazyWithPreload(() => import('./components/CheckoutApplePay'));

// promo65 ($65.00 sale) and promo49 ($49.95 sale) funnels — independent copies
// of the eligibility -> qualify -> shipping -> checkout -> confirmation flow, each
// with its own pricing/PID. Both hand off to the shared /important page.
const Promo65EligibilityCheck = lazyWithPreload(() => import('./components/promo65/EligibilityCheck'));
const Promo65QualificationFlow = lazyWithPreload(() => import('./components/promo65/QualificationFlow'));
const Promo65Shipping = lazyWithPreload(() => import('./components/promo65/Shipping'));
const Promo65Checkout = lazyWithPreload(() => import('./components/promo65/Checkout'));
const Promo65Confirmation = lazyWithPreload(() => import('./components/promo65/Confirmation'));

const Promo49EligibilityCheck = lazyWithPreload(() => import('./components/promo49/EligibilityCheck'));
const Promo49QualificationFlow = lazyWithPreload(() => import('./components/promo49/QualificationFlow'));
const Promo49Shipping = lazyWithPreload(() => import('./components/promo49/Shipping'));
const Promo49Checkout = lazyWithPreload(() => import('./components/promo49/Checkout'));
const Promo49Confirmation = lazyWithPreload(() => import('./components/promo49/Confirmation'));

const FAQ = lazyWithPreload(() => import('./pages/FAQ'));
const TermsConditions = lazyWithPreload(() => import('./pages/Termsconditions'));
const PrivacyPolicy = lazyWithPreload(() => import('./pages/Privacypolicy'));
const StateRecognition = lazyWithPreload(() => import('./pages/Staterecognition'));
const ContactUs = lazyWithPreload(() => import('./pages/Contactus'));

// ─── Idle preload ────────────────────────────────────────────────────────────
// After the main thread is free, silently fetch all lazy chunks so subsequent
// navigations are instant. requestIdleCallback fires when browser is not busy.

const idlePreloadAll = () => {
  const all = [
    Trustbar, Safety, HowItWorks, DontWait, Testimonials, FAQSection,
    EligibilityCheck, QualificationFlow, Shipping, Checkout,
    Confirmation, QualifyBanner, Important,
    Promo65EligibilityCheck, Promo65QualificationFlow, Promo65Shipping,
    Promo65Checkout, Promo65Confirmation,
    Promo49EligibilityCheck, Promo49QualificationFlow, Promo49Shipping,
    Promo49Checkout, Promo49Confirmation,
    FAQ, TermsConditions, PrivacyPolicy, StateRecognition, ContactUs,
  ];
  all.forEach((c) => c.preload());
};

if ('requestIdleCallback' in window) {
  // ✅ Wait until browser is idle (after LCP, TTI) then prefetch all chunks
  requestIdleCallback(idlePreloadAll, { timeout: 3000 });
} else {
  // Fallback for Safari
  setTimeout(idlePreloadAll, 3000);
}

// ────────────────────────────────────────────────────────────────────────────

// Apple Pay demo flag: set from ?applepay=1 on the homepage, read by Shipping
// on submit to decide whether to redirect to the apple-pay-demo checkout.
const APPLE_PAY_DEMO_KEY = 'applePayDemoExpiry';
const APPLE_PAY_DEMO_TTL_MS = 10 * 60 * 1000; // 10 minutes

const Common = () => (
  <>
    <ScrollToTop />
    <Header />
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
    <Footer />
  </>
);

const Landing = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('applepay') === '1') {
      localStorage.setItem(APPLE_PAY_DEMO_KEY, String(Date.now() + APPLE_PAY_DEMO_TTL_MS));
    }
  }, []);

  return (
  <>
    <Hero />

    {/* Mobile only */}
    <div className="trustbar-mob">
      <Trustbar />
    </div>
    <div className="how-its-mob">
      <HowItWorks />
    </div>
    <div className="dont-wait-mob">
      <DontWait />
    </div>

    <div className="trustbar-desk">
      <Trustbar />
    </div>
    <Safety />

    {/* Desktop only */}
    <div className="how-its-desk">
      <HowItWorks />
    </div>
    <div className="dont-wait-desk">
      <DontWait />
    </div>

    <Testimonials />

    <FAQSection />

    {/* Mobile only */}
    <div className="qualify-banner-mob">
      <QualifyBanner />
    </div>
  </>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Common />,
    children: [
      { index: true, element: <Landing /> },
      { path: 'eligibility', element: <EligibilityCheck /> },
      { path: 'qualify', element: <QualificationFlow /> },
      { path: 'shipping', element: <Shipping /> },
      { path: 'checkout', element: <CheckoutApplePay /> },
      // { path: 'checkout', element: <Checkout /> },
      { path: 'confirmation', element: <Confirmation /> },
      { path: 'important', element: <Important /> },
      { path: 'faq', element: <FAQ /> },
      { path: 'terms-conditions', element: <TermsConditions /> },
      { path: 'privacy-policy', element: <PrivacyPolicy /> },
      { path: 'state-recognition', element: <StateRecognition /> },
      { path: 'contact-us', element: <ContactUs /> },
      // { path: 'apple-pay-demo', element: <CheckoutApplePay /> },

      {
        path: 'promo65',
        element: <Outlet />,
        children: [
          { index: true, element: <Landing /> },
          { path: 'eligibility', element: <Promo65EligibilityCheck /> },
          { path: 'qualify', element: <Promo65QualificationFlow /> },
          { path: 'shipping', element: <Promo65Shipping /> },
          { path: 'checkout', element: <Promo65Checkout /> },
          { path: 'confirmation', element: <Promo65Confirmation /> },
        ],
      },
      {
        path: 'promo49',
        element: <Outlet />,
        children: [
          { index: true, element: <Landing /> },
          { path: 'eligibility', element: <Promo49EligibilityCheck /> },
          { path: 'qualify', element: <Promo49QualificationFlow /> },
          { path: 'shipping', element: <Promo49Shipping /> },
          { path: 'checkout', element: <Promo49Checkout /> },
          { path: 'confirmation', element: <Promo49Confirmation /> },
        ],
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

reportWebVitals();